import _ from "lodash";
import { flow, types } from "mobx-state-tree";
import { getEnv } from "~/mobx/utils/getEnv";
import { getRoot } from "~/mobx/utils/getRoot";
import { assert } from "~/utils/assert";
import { User } from "./User";
import type { UserInstance, UserSnapshotIn } from "./User";
import type { AxiosResponse } from "axios";
import type { Instance, SnapshotIn, SnapshotOut } from "mobx-state-tree";

export interface UserStoreInstance extends Instance<typeof UserStore> {}
export interface UserStoreSnapshotIn extends SnapshotIn<typeof UserStore> {}
export interface UserStoreSnapshotOut extends SnapshotOut<typeof UserStore> {}

type UserInfo = {
  firstName: string;
  lastName: string;
  streetAddress: string;
  zipCode: string;
  city: string;
  countryCode: string;
  email: string;
  phoneNumber: string;
};

export const UserStore = types
  .model("UserStore", {
    map: types.map(User),
  })
  .actions((self) => {
    return {
      processOne(data: UserSnapshotIn) {
        const existing = self.map.get(data.id);
        if (existing) {
          _.mergeWith(existing, data, (_, next) => {
            if (Array.isArray(next)) return next; // Treat arrays like atoms
          });
          return existing;
        }
        return self.map.put(data as UserSnapshotIn);
      },
    };
  })
  .actions((self) => {
    return {
      updateActiveUser: flow<UserInstance, [Partial<UserInfo>]>(
        function* updateActiveUser(userInfo): any {
          const env = getEnv(self);
          const root = getRoot(self);

          const activeUser = root.authStore.activeUser;

          assert(activeUser, "No active user");

          const data: UserInfo = {
            firstName: activeUser.firstName,
            lastName: activeUser.lastName,
            streetAddress: activeUser.streetAddress,
            zipCode: activeUser.zipCode,
            city: activeUser.city,
            countryCode: activeUser.countryCode ?? "SE",
            email: activeUser.email,
            phoneNumber: activeUser.phoneNumber,
            ...userInfo,
          };

          const response: AxiosResponse = yield env.baseHttp.patch(
            "clients/me",
            data,
          );

          const user = response.data.data;

          response.data = root.userStore.processOne(user);

          return response.data;
        },
      ),
    };
  });
